import { create } from 'zustand';
import { supabase, DBSupplier } from '../lib/supabase';
import { getActiveCollectionId } from './collectionStore';

export interface Supplier {
  id: string;
  name: string;
  createdAt: Date;
}

interface SuppliersState {
  suppliers: Supplier[];
  isLoading: boolean;
  error: string | null;

  fetchSuppliers: () => Promise<void>;
  addSupplier: (name: string) => Promise<boolean>;
  updateSupplier: (id: string, name: string) => Promise<boolean>;
  removeSupplier: (id: string) => Promise<boolean>;
}

const toSupplier = (row: DBSupplier): Supplier => ({
  id: row.id,
  name: row.name,
  createdAt: new Date(row.created_at),
});

let suppliersFetchGen = 0;

export const useSuppliersStore = create<SuppliersState>((set, get) => ({
  suppliers: [],
  isLoading: false,
  error: null,

  fetchSuppliers: async () => {
    const collectionId = getActiveCollectionId();
    if (!collectionId) {
      set({ suppliers: [], isLoading: false });
      return;
    }

    const gen = ++suppliersFetchGen;
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .eq('collection_id', collectionId)
        .order('name', { ascending: true });

      if (gen !== suppliersFetchGen) return;

      if (error) {
        set({ isLoading: false, error: error.message });
        return;
      }
      set({ suppliers: (data ?? []).map(toSupplier), isLoading: false });
    } catch (err) {
      if (gen !== suppliersFetchGen) return;
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load suppliers',
      });
    }
  },

  addSupplier: async (name: string) => {
    const collectionId = getActiveCollectionId();
    if (!collectionId) return false;

    const trimmed = name.trim();
    if (!trimmed) return false;

    set({ error: null });

    const { data, error } = await supabase
      .from('suppliers')
      .insert({ name: trimmed, collection_id: collectionId })
      .select()
      .single();

    if (error) {
      console.error('[addSupplier]', error);
      set({ error: error.message });
      return false;
    }
    if (data) {
      set({
        suppliers: [...get().suppliers, toSupplier(data)].sort((a, b) =>
          a.name.localeCompare(b.name)
        ),
      });
    }
    return true;
  },

  updateSupplier: async (id: string, name: string) => {
    const existing = get().suppliers.find(s => s.id === id);
    if (!existing) return false;

    const trimmed = name.trim();
    if (!trimmed || trimmed === existing.name) return true;

    set({ error: null });
    const { data, error } = await supabase
      .from('suppliers')
      .update({ name: trimmed })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      set({ error: error.message });
      return false;
    }
    if (!data) return false;

    set({
      suppliers: get().suppliers
        .map(s => (s.id === id ? toSupplier(data) : s))
        .sort((a, b) => a.name.localeCompare(b.name)),
    });
    return true;
  },

  removeSupplier: async (id: string) => {
    set({ error: null });
    const { error } = await supabase.from('suppliers').delete().eq('id', id);
    if (error) {
      set({ error: error.message });
      return false;
    }
    set({ suppliers: get().suppliers.filter(s => s.id !== id) });
    return true;
  },
}));
